import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Pause, Play, X } from "lucide-react";
import { useT } from "../lib/i18n";

/**
 * OperationalAutoTour — cinematic, idle-triggered auto-demonstration.
 *
 * THIS IS NOT AN ONBOARDING TOUR.
 *
 * After ~10 seconds of idle on /demo, this overlay fires the operational
 * causality loop ON ITS OWN so prospects watch the ecosystem absorb a
 * field submission across 8 connected surfaces:
 *
 *   T+0   submission detected (forgeops:submission event dispatched)
 *   T+3   ticker chip appears                  (ticker listener)
 *   T+5   workflow chain pulses                (chain panel listener)
 *   T+7   operational pressure tile +1         (pressure listener)
 *   T+10  ledger entry materializes            (ledger listener)
 *   T+14  exec heat-map reacts                 (exec listener)
 *   T+18  routed/PDF acknowledgement           (toast listener)
 *   T+22  archive confirmation                 (this overlay)
 *   T+30  loop completes — restarts after 12s
 *
 * Pauses INSTANTLY on any user interaction (mouse-move / scroll / key /
 * touch / click). A restrained "Replay live ops tour" pill in the
 * bottom-right lets the user re-arm.
 *
 * Visual contract: this overlay renders NO giant chrome — only a small
 * mono telemetry strip in the bottom-right corner. The actual proof of
 * causality happens on the existing operational surfaces; this
 * component is just the conductor.
 */

const IDLE_BEFORE_START_MS  = 10_000;
const IDLE_BEFORE_RESUME_MS = 18_000;
const POST_TOUR_GAP_MS      = 12_000;
const ROTATION_KEY          = "forgeops.autotour.scenario";


// ──────────────────────────── SCENARIOS ────────────────────────────
//
// Four operational chains rotate sequentially. Each tells a distinct
// causality story — different submission kind, different ecosystem
// reactions, different business consequence. Order persists across
// page reloads via sessionStorage so a returning prospect sees a
// fresh chain instead of the same one again.
//
// Every `fire` step dispatches the REAL `forgeops:submission` event.
// The existing 8 listeners (ticker, pressure, ledger, chain, exec
// heat-map, pain-relief, ops-feed, routing-toast) already branch on
// `detail.kind` and produce kind-specific reactions — we don't fake
// isolated animations; we drive the real architecture.

const SCENARIOS = [
  {
    id: "toolbox-talk",
    eyebrow: "Scenario · Safety & Compliance",
    steps: [
      {
        t: 0,
        label: "Toolbox Talk submitted · Crew C-3 · 7 attendees",
        fire: () =>
          window.dispatchEvent(
            new CustomEvent("forgeops:submission", {
              detail: {
                kind: "toolbox-talk",
                attendees: 7,
                crew: "C-3",
                job: "TEST-2098",
                topic: "Trench shoring · pre-task",
                __auto_tour: true,
              },
            }),
          ),
      },
      { t: 3000,  label: "Activity ticker · routed to crew profiles" },
      { t: 5000,  label: "Workflow chain · 6 nodes pulsing" },
      { t: 7000,  label: "Training records · attendance written to 7 employees" },
      { t: 10000, label: "Compliance counter · safety acks decremented" },
      { t: 14000, label: "PDF generated · DR-2098-TBT-EN.pdf" },
      { t: 18000, label: "Operations ledger · entry committed" },
      { t: 22000, label: "Archived · audit trail logged · same-day visibility" },
    ],
  },
  {
    id: "preop-fail",
    eyebrow: "Scenario · Fleet & Shop Readiness",
    steps: [
      {
        t: 0,
        label: "Equipment Pre-Op FAIL · Excavator EX-14 · hydraulic leak",
        fire: () =>
          window.dispatchEvent(
            new CustomEvent("forgeops:submission", {
              detail: {
                kind: "preop-fail",
                unit: "EX-14",
                fault: "Hydraulic leak · boom cylinder",
                operator: "Op-204",
                job: "TEST-2110",
                __auto_tour: true,
              },
            }),
          ),
      },
      { t: 2500,  label: "Equipment locked out · OOS flag raised" },
      { t: 5000,  label: "Shop alerted · maintenance queue +1" },
      { t: 7500,  label: "Superintendent notified · backfill request opened" },
      { t: 10500, label: "Pressure panel · equipment lockouts +1" },
      { t: 14000, label: "Replacement EX-09 dispatched · ETA 47 min" },
      { t: 18000, label: "Fleet uptime KPI · adjusted 94.2% → 92.8%" },
      { t: 22000, label: "Operations ledger · lockout audit trail logged" },
    ],
  },
  {
    id: "incident",
    eyebrow: "Scenario · Executive Risk Visibility",
    steps: [
      {
        t: 0,
        label: "Incident submitted · Near-miss · Crew B-2 · stop-the-line",
        fire: () =>
          window.dispatchEvent(
            new CustomEvent("forgeops:submission", {
              detail: {
                kind: "near-miss",
                severity: "stop-the-line",
                crew: "B-2",
                job: "TEST-2098",
                description: "Unsecured load · overhead",
                __auto_tour: true,
              },
            }),
          ),
      },
      { t: 3000,  label: "Safety escalation triggered · safety lead paged" },
      { t: 6000,  label: "Executive heat-map · safety pressure absorbed" },
      { t: 9500,  label: "Risk pressure · open incidents +1" },
      { t: 13000, label: "Corrective action queue · CA-2098-04 created" },
      { t: 16500, label: "Escalation routing · CFO + COO notified" },
      { t: 20000, label: "Compliance priority elevated · 14d follow-up scheduled" },
      { t: 23000, label: "Operations ledger · incident archive sealed" },
    ],
  },
  {
    id: "daily-report",
    eyebrow: "Scenario · Production & PM Intelligence",
    steps: [
      {
        t: 0,
        label: "Daily Report submitted · DR-2098 · Crew A-1 · 8.5h",
        fire: () =>
          window.dispatchEvent(
            new CustomEvent("forgeops:submission", {
              detail: {
                kind: "daily-report",
                crew_size: 9,
                hours: 8.5,
                qty_cy: 142,
                job: "TEST-2098",
                __auto_tour: true,
              },
            }),
          ),
      },
      { t: 2500,  label: "Production logged · 142 CY concrete placed" },
      { t: 5000,  label: "Manpower roll · 9 ops · 8.5h total" },
      { t: 8000,  label: "PM routing · auto-routed to PM-Whitman" },
      { t: 11500, label: "Production dashboard · DR-2098 refreshed" },
      { t: 15000, label: "Executive KPIs · daily throughput +1" },
      { t: 19000, label: "Archive synchronization · PM folder + Job folder" },
      { t: 23000, label: "Operations ledger · production audit trail logged" },
    ],
  },
];

function _readScenarioIdx() {
  try {
    const raw = window.sessionStorage.getItem(ROTATION_KEY);
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) return ((n % SCENARIOS.length) + SCENARIOS.length) % SCENARIOS.length;
  } catch { /* */ }
  return 0;
}

function _writeScenarioIdx(idx) {
  try { window.sessionStorage.setItem(ROTATION_KEY, String(idx)); } catch { /* */ }
}


export default function OperationalAutoTour({ enabled = true }) {
  const { t } = useT();
  const [running, setRunning]   = useState(false);
  const [paused,  setPaused]    = useState(false);
  const [stepIdx, setStepIdx]   = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [scenarioIdx, setScenarioIdx] = useState(() => _readScenarioIdx());

  // Translate scenario eyebrows + every stage label on the fly so an
  // EN ↔ ES toggle while the tour is running flips the narration text
  // without restarting the chain.
  const scenarios = useMemo(
    () =>
      SCENARIOS.map((s) => ({
        ...s,
        eyebrow: t(s.eyebrow),
        steps: s.steps.map((step) => ({ ...step, label: t(step.label) })),
      })),
    [t],
  );
  const scenario = scenarios[scenarioIdx] || scenarios[0];
  const STEPS = scenario.steps;
  const TOUR_DURATION_MS = STEPS[STEPS.length - 1].t + 4000;

  // Refs for timers so user interaction can cancel mid-step.
  const idleTimerRef = useRef(null);
  const stepTimersRef = useRef([]);
  const tourTimerRef = useRef(null);

  const clearStepTimers = () => {
    stepTimersRef.current.forEach((id) => clearTimeout(id));
    stepTimersRef.current = [];
  };

  const stopTour = useCallback(() => {
    clearStepTimers();
    if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
    setRunning(false);
    setStepIdx(0);
  }, []);

  const startTour = useCallback(() => {
    if (dismissed) return;
    clearStepTimers();
    setRunning(true);
    setPaused(false);
    setStepIdx(0);
    STEPS.forEach((s, i) => {
      const id = setTimeout(() => {
        setStepIdx(i);
        if (typeof s.fire === "function") {
          try { s.fire(); } catch (e) { /* never break demo on listener throw */ }
        }
      }, s.t);
      stepTimersRef.current.push(id);
    });
    tourTimerRef.current = setTimeout(() => {
      stopTour();
      // Track completion for sales telemetry (best-effort, fire-and-forget).
      try {
        import("@/lib/demoAccess").then((m) => {
          m.trackEvent?.("auto_tour_completed", {
            scenario: SCENARIOS[scenarioIdx]?.id || `scenario-${scenarioIdx}`,
          });
        });
      } catch { /* */ }
      // Advance to the next scenario for the next idle-fire cycle.
      // Persist via sessionStorage so a refresh continues the rotation.
      setScenarioIdx((prev) => {
        const next = (prev + 1) % SCENARIOS.length;
        _writeScenarioIdx(next);
        return next;
      });
      scheduleNextIdleStart();
    }, TOUR_DURATION_MS);
  }, [dismissed, stopTour, STEPS, TOUR_DURATION_MS]);  // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleNextIdleStart = useCallback((delay = POST_TOUR_GAP_MS) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      startTour();
    }, delay);
  }, [startTour]);

  // Idle detection — any interaction resets the timer + pauses a running tour.
  useEffect(() => {
    if (!enabled || dismissed) return;
    const reset = () => {
      if (runningRef.current) {
        // Pause the running tour and re-schedule a longer idle window
        // before auto-resume so we don't fight an engaged user.
        clearStepTimers();
        if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
        setPaused(true);
        setRunning(false);
        scheduleNextIdleStart(IDLE_BEFORE_RESUME_MS);
        return;
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        startTour();
      }, IDLE_BEFORE_START_MS);
    };
    reset();
    const events = ["mousemove", "scroll", "keydown", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // NOTE: do NOT clear stepTimersRef here — that's owned by
      // startTour/stopTour. The previous version cleared it on every
      // `running` dep change, killing the scheduled steps moments
      // after they were created.
    };
  }, [enabled, dismissed, startTour, scheduleNextIdleStart]);

  const handleReplay = () => {
    setDismissed(false);
    setPaused(false);
    startTour();
  };

  const handleDismiss = () => {
    stopTour();
    setDismissed(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  };

  // Mirror `running` to a ref so the idle-interaction effect can read
  // current state without listing `running` in its dep array. Listing
  // it there causes the effect to re-run when startTour() flips running
  // true, and the cleanup phase kills the freshly-scheduled step
  // timers — the bug iter-55 testing agent caught.
  const runningRef = useRef(running);
  useEffect(() => { runningRef.current = running; }, [running]);

  if (!enabled) return null;

  // ─────────────────────────── visual layer ────────────────────────────
  //
  // While the tour is running we show a compact mono telemetry strip in
  // the bottom-right corner narrating which causality stage just fired.
  // No big arrows, no overlay dimming, no curtain — the proof happens on
  // the existing operational surfaces, this is just the conductor.
  //
  // When the tour is idle / paused / dismissed we show a restrained
  // "Replay live ops tour" pill so the prospect can re-arm explicitly.

  if (running) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[55] pointer-events-none"
        data-testid="auto-tour-running"
      >
        <div
          className="relative pointer-events-auto"
          style={{
            background: "rgba(8,8,8,0.94)",
            border: "1px solid #1F1F1F",
            borderLeft: "2px solid #FF6A00",
            boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
            padding: "10px 14px 10px 12px",
            minWidth: 320,
            maxWidth: 420,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: "#FF6A00",
                  boxShadow: "0 0 6px rgba(255,106,0,0.7)",
                }}
              />
              <span
                className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold"
                style={{ color: "#FF6A00" }}
              >
                {t("Auto-tour · Live")}
              </span>
              <span
                className="font-mono text-[9px] uppercase tracking-[0.2em]"
                style={{ color: "#666" }}
              >
                · {scenarioIdx + 1} / {scenarios.length}
              </span>
            </div>
            <button
              onClick={handleDismiss}
              aria-label="Dismiss auto-tour"
              className="p-1 hover:opacity-100 opacity-60 transition-opacity"
              data-testid="auto-tour-dismiss"
            >
              <X className="w-3 h-3" style={{ color: "#999" }} />
            </button>
          </div>
          <div
            className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1.5"
            style={{ color: "#999" }}
            data-testid="auto-tour-scenario"
          >
            {scenario.eyebrow}
          </div>
          <div
            className="font-mono text-[11px] leading-snug"
            style={{ color: "#E5E5E5" }}
            data-testid="auto-tour-stage"
          >
            <span style={{ color: "#666" }}>
              {String(stepIdx + 1).padStart(2, "0")}/{String(STEPS.length).padStart(2, "0")}
              {" · "}
            </span>
            {STEPS[stepIdx]?.label || ""}
          </div>
          <div className="mt-2 flex items-center gap-1 h-[2px]">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="flex-1 transition-colors duration-500"
                style={{
                  height: 2,
                  background: i <= stepIdx ? "#FF6A00" : "#222",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (dismissed) {
    return (
      <button
        onClick={handleReplay}
        className="fixed bottom-4 right-4 z-[55] flex items-center gap-2 pointer-events-auto"
        data-testid="auto-tour-replay-dismissed"
        style={{
          background: "rgba(8,8,8,0.92)",
          border: "1px solid #1F1F1F",
          color: "#BDBDBD",
          padding: "8px 12px",
          fontFamily: "ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <Play className="w-3 h-3" style={{ color: "#FF6A00" }} />
        {t("Replay live ops tour")}
      </button>
    );
  }

  // Idle (waiting to auto-start) — show a quiet hint pill so the prospect
  // knows the system is alive and waiting for them OR will demonstrate
  // itself shortly. The next scenario id surfaces in the pill so repeat
  // visitors see "different chains incoming" instead of the same one.
  const nextScenarioLabel = scenario.eyebrow
    .replace(/^Scenario · /, "")
    .replace(/^Escenario · /, "")
    .toLowerCase();

  return (
    <button
      onClick={handleReplay}
      className="fixed bottom-4 right-4 z-[55] flex items-center gap-2 pointer-events-auto group"
      data-testid="auto-tour-idle-pill"
      title={`Next scenario: ${scenario.eyebrow}`}
      style={{
        background: "rgba(8,8,8,0.7)",
        border: "1px solid #1A1A1A",
        color: "#666",
        padding: "7px 11px",
        fontFamily: "ui-monospace, monospace",
        fontSize: 9.5,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        transition: "color 200ms ease, border-color 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "#FF6A00";
        e.currentTarget.style.borderColor = "#2A2A2A";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "#666";
        e.currentTarget.style.borderColor = "#1A1A1A";
      }}
    >
      {paused ? (
        <>
          <Pause className="w-3 h-3" /> {t("Auto-tour · paused")}
        </>
      ) : (
        <>
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "#FF6A00" }}
          />
          {t("Auto-tour · armed")}
          <span
            className="ml-1 hidden sm:inline"
            style={{ color: "#444" }}
          >
            · {nextScenarioLabel}
          </span>
        </>
      )}
    </button>
  );
}
